import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiBook,
  FiUsers,
  FiClock,
  FiPlus,
  FiEye,
  FiSettings,
  FiGlobe,
  FiLock,
  FiUserPlus,
  FiMoreVertical,
  FiLogOut,
  FiAlertCircle,
  FiStar,
} from "react-icons/fi";
import communityService from "../../services/communityService";
import CreateCommunityRoomModal from "./CreateCommunityRoomModal";
import "./styles/index.css";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.3, ease: "easeOut" },
  },
};

const CommunityRooms = ({
  communityId,
  rooms: previewRooms,
  community,
  userRole,
  currentUser,
}) => {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  const [permissions, setPermissions] = useState({
    canViewRoomDetails: false,
    canJoinRooms: false,
    canCreateRooms: false,
    canViewMembers: false,
    canManageRoom: false,
    isMember: false,
    isViewOnly: false,
  });

  const canCreateRooms = userRole === "creator" || userRole === "moderator";

  useEffect(() => {
    fetchRooms();
  }, [communityId, previewRooms]);

  const fetchRooms = async () => {
    try {
      setLoading(true);

      if (
        false &&
        previewRooms &&
        previewRooms.list &&
        previewRooms.list.length > 0
      ) {
        const processedRooms = processRoomsForMembership(previewRooms.list);
        setRooms(processedRooms);
        setLoading(false);
        return;
      }

      const response = await communityService.getCommunityRooms(communityId, {
        page: 1,
        limit: 20,
        sortBy: "createdAt",
        sortOrder: "desc",
      });

      if (response.success && response.data) {
        const { rooms: roomsList, permissions: roomPermissions } =
          response.data;

        if (roomPermissions) {
          setPermissions(roomPermissions);
        }

        const processedRooms = processRoomsForMembership(roomsList || []);
        setRooms(processedRooms);
      } else {
        setRooms([]);
      }
    } catch (error) {
setRooms([]);

      if (error.status === 403) {
        setPermissions({
          canViewRoomDetails: false,
          canJoinRooms: false,
          canCreateRooms: false,
          canViewMembers: false,
          canManageRoom: false,
          isMember: false,
          isViewOnly: false,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const processRoomsForMembership = (rooms) => {
    const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
    const currentUserId = currentUser?.id || currentUser?._id;

    if (!currentUserId) {
      return rooms;
    }

    return rooms.map((room) => {
      if (
        room.userIsMember !== undefined &&
        room.userIsLeader !== undefined &&
        room.userHasRequested !== undefined
      ) {
        return room;
      }

      const isLeader =
        room.leader &&
        (room.leader.id === currentUserId || room.leader._id === currentUserId);

      const isMember =
        isLeader ||
        (Array.isArray(room.members) &&
          room.members.length > 0 &&
          room.members.some(
            (member) =>
              member.user?.id === currentUserId ||
              member.user?._id === currentUserId ||
              member.id === currentUserId ||
              member._id === currentUserId
          )) ||
        false;

      const hasRequested =
        (Array.isArray(room.joinRequests) &&
          room.joinRequests.length > 0 &&
          room.joinRequests.some((request) => {
            const requesterId =
              request.user?.id ||
              request.user?._id ||
              request.user ||
              request.id ||
              request._id;
            return requesterId === currentUserId;
          })) ||
        false;

      return {
        ...room,
        userIsMember: isMember,
        userIsLeader: isLeader,
        userHasRequested: hasRequested,
      };
    });
  };

  const handleCreateRoom = async (roomData) => {
    try {
      const response = await communityService.createCommunityRoom(
        communityId,
        roomData
      );

      if (response.success && response.data?.room) {
        const processedRoom = processRoomsForMembership([
          response.data.room,
        ])[0];

        setRooms((prev) => [processedRoom, ...prev]);
        setShowCreateModal(false);
        return response;
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
throw error;
    }
  };

  const handleJoinCommunity = () => {
    navigate(`/communities/${communityId}/preview`);
  };

  const handleJoinRoom = async (roomId) => {
    try {
      setActionLoading((prev) => ({ ...prev, [roomId]: "joining" }));

      const response = await communityService.requestToJoinRoom(
        communityId,
        roomId
      );
      if (response.success) {
        await fetchRooms();
      }
    } catch (error) { /* ignored */ } finally {
      setActionLoading((prev) => ({ ...prev, [roomId]: null }));
    }
  };

  const handleLeaveRoom = async (roomId) => {
    try {
      setActionLoading((prev) => ({ ...prev, [roomId]: "leaving" }));

      const response = await communityService.leaveRoom(communityId, roomId);
      if (response.success) {
        await fetchRooms();
      }
    } catch (error) { /* ignored */ } finally {
      setActionLoading((prev) => ({ ...prev, [roomId]: null }));
    }
  };

  const formatTimeAgo = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInMs = now - date;
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      const diffInHours = Math.floor(diffInMinutes / 60);
      const diffInDays = Math.floor(diffInHours / 24);

      if (diffInDays > 7) {
        return date.toLocaleDateString();
      } else if (diffInDays > 0) {
        return `${diffInDays}d ago`;
      } else if (diffInHours > 0) {
        return `${diffInHours}h ago`;
      } else if (diffInMinutes > 0) {
        return `${diffInMinutes}m ago`;
      } else {
        return "Just now";
      }
    } catch (error) {
      return "Unknown";
    }
  };

  const openRoomChat = (room) => {
    let normalizedRoomId = room._id || room.id;
    normalizedRoomId = String(normalizedRoomId);
    navigate(
      `/labs/${normalizedRoomId}?origin=community&communityId=${communityId}`
    );
  };

  const getRoomActionButton = (room) => {
    const roomId = room._id;
    const currentAction = actionLoading[roomId];

    if (permissions.isViewOnly) {
      if (permissions.canViewRoomDetails) {
        return (
          <motion.button
            className="rcard-action-btn view"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <FiEye size={13} />
            View
          </motion.button>
        );
      } else {
        return (
          <motion.button
            className="rcard-action-btn blue"
            onClick={handleJoinCommunity}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <FiUserPlus size={13} />
            Join
          </motion.button>
        );
      }
    }

    if (room.userIsMember) {
      if (room.userIsLeader) {
        return (
          <motion.button
            className="rcard-action-btn amber"
            onClick={() => openRoomChat(room)}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <FiSettings size={13} />
            Manage
          </motion.button>
        );
      }
      return (
        <div className="rcard-action-group">
          <motion.button
            className="rcard-action-btn green"
            onClick={() => openRoomChat(room)}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <FiEye size={13} />
            Enter
          </motion.button>
          <motion.button
            className="rcard-action-btn red"
            onClick={() => handleLeaveRoom(roomId)}
            disabled={currentAction === 'leaving'}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <FiLogOut size={13} />
            {currentAction === 'leaving' ? '...' : 'Leave'}
          </motion.button>
        </div>
      );
    }

    if (room.userHasRequested) {
      return (
        <motion.button className="rcard-action-btn pending" disabled>
          <FiClock size={13} />
          Pending
        </motion.button>
      );
    }

    if ((room.memberCount || 0) >= 8) {
      return (
        <motion.button className="rcard-action-btn full" disabled>
          <FiAlertCircle size={13} />
          Full
        </motion.button>
      );
    }

    return (
      <motion.button
        className="rcard-action-btn green"
        onClick={() => handleJoinRoom(roomId)}
        disabled={currentAction === 'joining' || !permissions.canJoinRooms}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
      >
        <FiUserPlus size={13} />
        {currentAction === 'joining' ? '...' : 'Request'}
      </motion.button>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="rcard-container">
        <div className="rcard-loading">
          <div className="rcard-spinner"></div>
          <p>Loading rooms...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rcard-container">
      {/* ===== HEADER ===== */}
      <motion.div 
        className="rcard-header"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="rcard-header-left">
          <div className="rcard-title-row">
            <div className="rcard-icon-wrap">
              <FiBook size={20} />
            </div>
            <h3>Research Rooms</h3>
          </div>
          <p className="rcard-subtitle">Collaborate on focused research topics</p>
        </div>

        {permissions.canCreateRooms && (
          <motion.button
            className="rcard-create-btn"
            onClick={() => setShowCreateModal(true)}
            whileHover={{ scale: 1.03, boxShadow: '0 6px 20px rgba(16, 185, 129, 0.35)' }}
            whileTap={{ scale: 0.97 }}
          >
            <FiPlus size={16} />
            <span>New Room</span>
          </motion.button>
        )}
      </motion.div>

      {/* ===== ROOMS GRID ===== */}
      <div className="rcard-content">
        {rooms.length > 0 ? (
          <motion.div
            className="rcard-grid"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {rooms.map((room) => (
              <motion.div
                key={room._id}
                className={`rcard-card ${room.userIsMember ? 'member' : ''} ${room.userIsLeader ? 'leader' : ''}`}
                variants={cardVariants}
                whileHover={{ y: -4, boxShadow: '0 12px 32px rgba(0, 0, 0, 0.1)' }}
              >
                {/* Card Header */}
                <div className="rcard-card-header">
                  <div className="rcard-card-info">
                    <h4 className="rcard-card-name">{room.name}</h4>
                    <div className="rcard-badges">
                      <span className={`rcard-badge ${room.type}`}>
                        {room.type === 'public' ? <FiGlobe size={10} /> : <FiLock size={10} />}
                        {room.type}
                      </span>
                      {room.userIsLeader && (
                        <span className="rcard-badge leader">
                          <FiStar size={10} />
                          Leader
                        </span>
                      )}
                      {permissions.isViewOnly && (
                        <span className="rcard-badge readonly">
                          <FiEye size={10} />
                          View
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="rcard-card-actions">
                    {(room.userIsMember || permissions.canViewRoomDetails) && (
                      <motion.button
                        className="rcard-icon-btn"
                        onClick={() => openRoomChat(room)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FiEye size={15} />
                      </motion.button>
                    )}
                    {(room.userIsLeader || (canCreateRooms && !permissions.isViewOnly)) && (
                      <motion.button
                        className="rcard-icon-btn"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FiMoreVertical size={15} />
                      </motion.button>
                    )}
                  </div>
                </div>

                {/* Description */}
                <p className="rcard-card-desc">{room.description}</p>

                {/* Field Tags */}
                <div className="rcard-tags">
                  <span className="rcard-tag primary">{room.field}</span>
                  {room.subField && room.subField !== room.field && (
                    <span className="rcard-tag secondary">{room.subField}</span>
                  )}
                </div>

                {/* Stats Row */}
                <div className="rcard-stats">
                  <div className="rcard-stat">
                    <FiUsers size={14} />
                    <span>{room.memberCount || 0}/8</span>
                  </div>
                  <div className="rcard-stat">
                    <FiClock size={14} />
                    <span>
                      {room.lastActivity
                        ? formatTimeAgo(room.lastActivity)
                        : room.createdAt
                        ? formatTimeAgo(room.createdAt)
                        : 'New'}
                    </span>
                  </div>
                </div>

                {/* Footer */}
                <div className="rcard-card-footer">
                  <div className="rcard-leader">
                    <span>Led by <strong>{room.leader?.name || room.createdBy?.name || 'Unknown'}</strong></span>
                  </div>
                  {getRoomActionButton(room)}
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            className="rcard-empty"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="rcard-empty-icon">
              <FiBook size={40} />
            </div>
            <h4>No Research Rooms Yet</h4>
            <p>Focused spaces for collaboration on specific topics.</p>

            {permissions.isViewOnly ? (
              <div className="rcard-empty-cta">
                <p className="rcard-note">Join the community to access rooms.</p>
                <motion.button
                  className="rcard-cta-btn blue"
                  onClick={handleJoinCommunity}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <FiUserPlus size={16} />
                  Join Community
                </motion.button>
              </div>
            ) : permissions.canCreateRooms ? (
              <motion.button
                className="rcard-cta-btn green"
                onClick={() => setShowCreateModal(true)}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <FiPlus size={16} />
                Create First Room
              </motion.button>
            ) : (
              <p className="rcard-note">Only moderators can create rooms.</p>
            )}
          </motion.div>
        )}
      </div>

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && permissions.canCreateRooms && (
          <CreateCommunityRoomModal
            communityId={communityId}
            community={community}
            onClose={() => setShowCreateModal(false)}
            onSuccess={handleCreateRoom}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default CommunityRooms;
